import { Box } from "@mui/material";
import clsx from "clsx";
import i18next from "i18next";
import { toJS } from "mobx";
import { observer } from "mobx-react-lite";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import BriaButton from "../../../components/common/BriaButton/BriaButton";
import { FilterByOptions, PaginationOptions } from "../../../components/common/BriaTable/BriaTable";
import ProjectCard from "../../../components/common/ProjectCard/ProjectCard";
import RouterConstants from "../../../constants/RouterConstants";
import { getSelectedOrganization } from "../../../helpers/localStorage";
import useSecureNavigate from "../../../hooks/useSecureNavigate";
import { useAppStore } from "../../../hooks/useStores";
import DataDashboardLayout from "../../../layout/DataDashboard/DataDashboardLayout";
import { formatDate } from "../../../utils";
import iframeStore from "../../IframeNew/iframe-store";
import { isLegacyTgRoute } from "../utils/tg-utils";
import TgProjectActionsPopover from "./Dashboard/ActionsPopover/TgProjectActionsPopover";
import TgProjectsEmpty from "./EmptyState/TgProjectsEmpty";
import styles from "./TgProjects.module.scss";
import { ViewTgProjectLocationState } from "./Viewer/TgProjectViewer";
import { TgProject } from "./store/tg-projects-store";

const TgProjects = () => {
	const { t } = useTranslation("tg", { keyPrefix: "projects.dashboard" });
	const navigate = useSecureNavigate();
	const { tgStore, authStore, uiStore } = useAppStore();
	const { tgProjectStore } = tgStore;
	const [filterBy, setFilterBy] = useState<FilterByOptions<TgProject>>();

	const withRouteFilter = (base?: FilterByOptions<TgProject>): FilterByOptions<TgProject> => {
		const legacy = isLegacyTgRoute();
		const merged: FilterByOptions<TgProject> = {
			...(base || {}),
			filters: { ...(base?.filters || {}) },
		};
		if (!legacy) {
			(merged.filters as any).training_version = "fibo";
		}
		return merged;
	};

	const routeFilterBy = withRouteFilter(filterBy);
	const items = tgProjectStore.paginatedProjects.items || [];
	const filteredItems = isLegacyTgRoute()
		? items.filter((p) => p.training_version !== "fibo")
		: items.filter((p) => p.training_version === "fibo");

	const paginationOptions: PaginationOptions<TgProject> | undefined =
		filteredItems.length > 0
			? {
					rowsPerPage: tgProjectStore.rowsPerPage,
					totalRows: tgProjectStore.paginatedProjects.total,
					loadNextPage: tgProjectStore.getOrgProjects,
				}
			: undefined;

	useEffect(() => {
		void tgProjectStore.getOrgProjects(1, withRouteFilter(filterBy));
		// Mount-only: search updates call `getOrgProjects` explicitly.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const openProject = (projectToView: TgProject) => {
		const viewProjectState: ViewTgProjectLocationState = { projectToView: toJS(projectToView) };
		const isFibo = projectToView.training_version === "fibo";
		const base = isFibo
			? RouterConstants.NEW_TG.PROJECTS.fullPath
			: RouterConstants.NEW_TG.LEGACY.PROJECTS.fullPath;
		navigate(`${base}/${projectToView.id}/${RouterConstants.NEW_TG.MODELS.path}`, {
			state: { ...viewProjectState },
		});
	};

	const openCreateProject = () => {
		if (!getSelectedOrganization()) {
			uiStore.showDialog("CreateOrganizationDialog");
			return;
		}
		const isLegacyRoute = isLegacyTgRoute();
		navigate(
			isLegacyRoute
				? RouterConstants.NEW_TG.LEGACY.PROJECTS.createPath
				: RouterConstants.NEW_TG.PROJECTS.createPath,
		);
	};

	const handleSearchChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
		const updatedFilterBy: FilterByOptions<TgProject> = {
			...filterBy,
			search: {
				text: e.target.value,
				fields: ["project_name", "project_description", "ip_medium", "ip_type"],
			},
		};
		setFilterBy(updatedFilterBy);
		await tgProjectStore.getOrgProjects(1, withRouteFilter(updatedFilterBy));
	};

	return (
		<DataDashboardLayout
			className={clsx({ [styles.layout]: true, [styles.iframeLayout]: iframeStore.isIframe() })}
			primaryButton={
				<BriaButton buttonType="primaryMedium" onClick={openCreateProject}>
					{t("primaryButton")}
				</BriaButton>
			}
			secondaryButton={
				authStore.user?.isSuperAdmin() ? (
					<BriaButton
						buttonType="secondaryMedium"
						onClick={tgProjectStore.loadTgUsageReport}
						loading={tgProjectStore.loadingUsageReport}
						disabled={tgProjectStore.loadingUsageReport}
					>
						{t("secondaryButton")}
					</BriaButton>
				) : undefined
			}
			enableSearch
			searchInputVisible
			handleSearchChange={handleSearchChange}
			searchBoxPlaceholder={t("search.placeholder")}
			paginationOptions={paginationOptions}
			filterBy={routeFilterBy}
			loading={tgProjectStore.loadingOrgProjects}
		>
			{!filteredItems.length ? (
				<TgProjectsEmpty />
			) : (
				<Box className={styles.cards}>
					{filteredItems.map((project) => {
						const cardFields = [
							{
								key: t("card.info.ip_medium"),
								value: i18next.t(`projects.editor.tgIpMediums.${project.ip_medium}`, { ns: "tg" }),
							},
							{
								key: t("card.info.ip_type"),
								value: i18next.t(`projects.editor.ipType.types.${project.ip_type}.name`, { ns: "tg" }),
							},
							{
								key: t("card.info.creation_date"),
								value: formatDate(project.created_at),
							},
						];
						return (
							<ProjectCard
								key={project.id}
								name={project.project_name}
								description={project.project_description}
								cardFields={cardFields}
								onSecondaryAction={() => openProject(project)}
								secondaryButtonLabel={t("card.actions.primaryButton.nonEmpty")}
								actionsPopover={<TgProjectActionsPopover project={project} />}
							/>
						);
					})}
				</Box>
			)}
		</DataDashboardLayout>
	);
};

export default observer(TgProjects);
